import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  FormGroup,
  Select,
  MenuItem,
  Grid,
  TextField,
  Button,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import HotelIcon from "@material-ui/icons/Hotel";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  ROOM_TYPES,
  BED_TYPES,
  AMENITIES_LIST,
  RULES_LIST,
} from "src/utils/misc";
import { asyncAddHotelRooms, toggleTab } from "src/actions/HomeActions";
import api from "src/utils/api";

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  accentDark: "#000000",
  divider: "#F1F5F9",
};

const INITIAL_VALUES = {
  room_type_count: 1,
  room_description: "",
  base_price: "",
  number_of_guests: "",
};

const useStyles = makeStyles(() => ({
  section: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    marginBottom: 20,
    overflow: "hidden",
  },
  sectionHeader: {
    padding: "20px 24px 0 24px",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: TOKENS.textPrimary,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: TOKENS.textSecondary,
    marginTop: 2,
  },
  sectionContent: {
    padding: "16px 24px 24px 24px",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 14,
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#A5B4FC" },
      "&.Mui-focused fieldset": { borderColor: TOKENS.accent },
    },
    "& .MuiInputLabel-root": { fontSize: 14, color: TOKENS.textSecondary },
  },
  selectField: {
    borderRadius: 8,
    fontSize: 14,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A5B4FC" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: TOKENS.accent,
    },
  },
  chipSelected: {
    backgroundColor: `${TOKENS.accent} !important`,
    color: "#FFFFFF !important",
    borderColor: `${TOKENS.accent} !important`,
    fontWeight: 500,
  },
  chipUnselected: {
    backgroundColor: "#FFFFFF",
    color: "#475569",
    borderColor: "#E2E8F0",
    fontWeight: 400,
    "&:hover": { backgroundColor: "#F8FAFC", borderColor: "#A5B4FC" },
  },
  chipGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  bedRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    backgroundColor: "#FAFBFC",
    borderRadius: 10,
    border: `1px solid ${TOKENS.border}`,
    marginBottom: 8,
  },
  addRowBtn: {
    color: TOKENS.accent,
    borderColor: TOKENS.border,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": { borderColor: "#A5B4FC", backgroundColor: "#EEF2FF" },
  },
  varianceRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    backgroundColor: "#FAFBFC",
    borderRadius: 10,
    border: `1px solid ${TOKENS.border}`,
    marginBottom: 8,
  },

  /* ── Saved rooms ─────────────────────────────────────────────── */
  savedRoomCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    backgroundColor: "#FAFBFC",
    borderRadius: 12,
    border: `1px solid ${TOKENS.border}`,
    marginBottom: 10,
    transition: "border-color 0.15s ease",
    "&:hover": {
      borderColor: "#A5B4FC",
    },
  },
  savedRoomInfo: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flex: 1,
  },
  savedRoomIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "#EEF2FF",
    color: TOKENS.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  savedRoomTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKENS.textPrimary,
    lineHeight: 1.3,
  },
  savedRoomMeta: {
    fontSize: 12,
    color: TOKENS.textMuted,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  savedRoomMetaDot: {
    width: 3,
    height: 3,
    borderRadius: 2,
    backgroundColor: TOKENS.textMuted,
  },
  savedRoomDeleteBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    color: TOKENS.textMuted,
    "&:hover": {
      backgroundColor: "#FEF2F2",
      color: "#EF4444",
    },
  },
  emptyRoomsHint: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 18px",
    backgroundColor: "#FFFBEB",
    borderRadius: 10,
    border: "1px solid #FEF3C7",
    fontSize: 13,
    color: "#92400E",
    fontWeight: 500,
  },

  /* ── Buttons ─────────────────────────────────────────────────── */
  saveRoomButton: {
    backgroundColor: TOKENS.surface,
    color: TOKENS.accent,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 8,
    padding: "12px 28px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": {
      borderColor: "#A5B4FC",
      backgroundColor: "#EEF2FF",
    },
    "&:disabled": {
      color: TOKENS.textMuted,
      borderColor: TOKENS.border,
    },
  },
  continueButton: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "12px 32px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(34, 34, 34, 0.3)",
    "&:hover": { backgroundColor: TOKENS.accentDark },
    "&:disabled": { backgroundColor: "#C7D2FE", color: "#FFFFFF" },
  },
}));

export default function RoomsTab() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const formikRef = useRef();

  const { hotel, add_hotel } = useSelector((state) => state.homes);
  const hotelData = hotel || add_hotel;

  // Saved rooms list
  const [savedRooms, setSavedRooms] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Current room form state (non-Formik fields)
  const [roomType, setRoomType] = useState("");
  const [roomBeds, setRoomBeds] = useState([{ bed_type: "", bed_count: 1 }]);
  const [priceVariance, setPriceVariance] = useState([]);
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [roomRules, setRoomRules] = useState([]);

  const toggleAmenity = (name) => {
    setRoomAmenities((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  const toggleRule = (name) => {
    setRoomRules((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  const resetFormForNextRoom = () => {
    setRoomType("");
    setRoomBeds([{ bed_type: "", bed_count: 1 }]);
    setPriceVariance([]);
    setRoomAmenities([]);
    setRoomRules([]);
    formikRef.current?.resetForm({ values: INITIAL_VALUES });
  };

  const handleSaveRoom = async (values, { setSubmitting }) => {
    if (!roomType) {
      toast.error("Please select a room type");
      setSubmitting(false);
      return;
    }

    setIsSaving(true);
    const payload = {
      ...values,
      room_type: roomType,
      room_name: roomType,
      room_amenities: roomAmenities.map((name) => ({ name })),
      room_beds: roomBeds.map((r) => ({
        name: r.bed_type,
        count: r.bed_count,
      })),
      room_rules: roomRules.map((name) => ({ name })),
      price_variance: priceVariance.map((item) => ({
        guest_count: item.guestCount,
        price: item.price,
      })),
      base_price: parseFloat(values.base_price),
      room_images: [],
    };

    try {
      const savedRoom = await dispatch(
        asyncAddHotelRooms(payload, hotelData, hotel ? true : false, "rooms"),
      );

      setSavedRooms((prev) => [
        ...prev,
        {
          id: savedRoom?.id || `temp-${Date.now()}`,
          room_type: roomType,
          room_type_count: values.room_type_count,
          number_of_guests: values.number_of_guests,
          base_price: values.base_price,
        },
      ]);

      toast.success(`${roomType} room saved!`);
      resetFormForNextRoom();
    } catch (error) {
      toast.error("Could not save room. Please try again.");
    } finally {
      setSubmitting(false);
      setIsSaving(false);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm("Are you sure you want to remove this room?")) return;

    // If it's a real backend ID, delete from server
    if (!String(roomId).startsWith("temp-")) {
      try {
        await api.delete(`hotels/rooms/?id=${roomId}`);
      } catch (error) {
        toast.error("Could not delete room.");
        return;
      }
    }

    setSavedRooms((prev) => prev.filter((r) => r.id !== roomId));
    toast.success("Room removed");
  };

  return (
    <>
      {/* ── Saved Rooms ──────────────────────────────────────────── */}
      <Card className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>Rooms Added</Typography>
          <Typography className={classes.sectionSubtitle}>
            {savedRooms.length > 0
              ? `${savedRooms.length} room type${savedRooms.length > 1 ? "s" : ""} saved — add more or continue to images`
              : "Save at least one room before continuing"}
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent}>
          {savedRooms.length === 0 ? (
            <Box className={classes.emptyRoomsHint}>
              No rooms added yet. Fill in the form below and click "Save Room".
            </Box>
          ) : (
            savedRooms.map((room) => (
              <Box key={room.id} className={classes.savedRoomCard}>
                <Box className={classes.savedRoomInfo}>
                  <Box className={classes.savedRoomIcon}>
                    <HotelIcon style={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography className={classes.savedRoomTitle}>
                      {room.room_type}
                    </Typography>
                    <Box className={classes.savedRoomMeta}>
                      <span>
                        {room.room_type_count} room
                        {Number(room.room_type_count) > 1 ? "s" : ""}
                      </span>
                      <span className={classes.savedRoomMetaDot} />
                      <span>
                        {room.number_of_guests} guest
                        {Number(room.number_of_guests) > 1 ? "s" : ""}
                      </span>
                      <span className={classes.savedRoomMetaDot} />
                      <span>${room.base_price}/night</span>
                    </Box>
                  </Box>
                </Box>
                <Tooltip title="Remove room">
                  <IconButton
                    className={classes.savedRoomDeleteBtn}
                    size="small"
                    onClick={() => handleDeleteRoom(room.id)}
                  >
                    <DeleteOutlineIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      {/* ── Room Form ────────────────────────────────────────────── */}
      <Formik
        innerRef={formikRef}
        initialValues={INITIAL_VALUES}
        validationSchema={Yup.object().shape({
          room_type_count: Yup.number().min(1).required("Required"),
          room_description: Yup.string().required("Please enter a description"),
          base_price: Yup.number()
            .min(1)
            .required("Please enter the base price"),
          number_of_guests: Yup.number()
            .min(1)
            .required("Please enter guest capacity"),
        })}
        onSubmit={handleSaveRoom}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            {/* Section header for the form */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  {savedRooms.length > 0 ? "Add Another Room" : "Room Details"}
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Type, count, capacity, and description
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                    >
                      <InputLabel>Room Type</InputLabel>
                      <Select
                        className={classes.selectField}
                        onChange={(e) => setRoomType(e.target.value)}
                        label="Room Type"
                        value={roomType}
                      >
                        {ROOM_TYPES.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(
                        touched.room_type_count && errors.room_type_count,
                      )}
                      helperText={
                        touched.room_type_count && errors.room_type_count
                      }
                      variant="outlined"
                      required
                      fullWidth
                      label="How Many Rooms?"
                      name="room_type_count"
                      type="number"
                      inputProps={{ min: 1 }}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(
                        touched.number_of_guests && errors.number_of_guests,
                      )}
                      helperText={
                        touched.number_of_guests && errors.number_of_guests
                      }
                      variant="outlined"
                      required
                      fullWidth
                      label="Max Guests"
                      name="number_of_guests"
                      type="number"
                      inputProps={{ min: 1 }}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(
                        touched.room_description && errors.room_description,
                      )}
                      helperText={
                        touched.room_description && errors.room_description
                      }
                      variant="outlined"
                      required
                      fullWidth
                      multiline
                      minRows={2}
                      label="Room Description"
                      name="room_description"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Describe the room and what makes it comfortable"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Beds */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Bed Configuration
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  What beds are available in this room?
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                {roomBeds.map((bed, index) => (
                  <Box key={index} className={classes.bedRow}>
                    <FormControl
                      variant="outlined"
                      style={{ flex: 1 }}
                      className={classes.textField}
                    >
                      <InputLabel>Bed Type</InputLabel>
                      <Select
                        className={classes.selectField}
                        onChange={(e) => {
                          const updated = [...roomBeds];
                          updated[index].bed_type = e.target.value;
                          setRoomBeds(updated);
                        }}
                        label="Bed Type"
                        value={bed.bed_type}
                      >
                        {BED_TYPES.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      label="Count"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={bed.bed_count}
                      onChange={(e) => {
                        const updated = [...roomBeds];
                        updated[index].bed_count = e.target.value;
                        setRoomBeds(updated);
                      }}
                      style={{ width: 100 }}
                    />
                    {roomBeds.length > 1 && (
                      <IconButton
                        size="small"
                        style={{ color: "#EF4444" }}
                        onClick={() =>
                          setRoomBeds(roomBeds.filter((_, i) => i !== index))
                        }
                      >
                        <RemoveIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  className={classes.addRowBtn}
                  startIcon={<AddIcon style={{ fontSize: 16 }} />}
                  onClick={() =>
                    setRoomBeds([...roomBeds, { bed_type: "", bed_count: 1 }])
                  }
                >
                  Add Another Bed
                </Button>
              </CardContent>
            </Card>

            {/* Amenities */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Room Amenities
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  What can guests use in this room? —{" "}
                  {roomAmenities.length > 0
                    ? `${roomAmenities.length} selected`
                    : "none selected"}
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Box className={classes.chipGrid}>
                  {AMENITIES_LIST.map((name) => (
                    <Chip
                      key={name}
                      label={roomAmenities.includes(name) ? `✓ ${name}` : name}
                      clickable
                      variant="outlined"
                      size="small"
                      className={
                        roomAmenities.includes(name)
                          ? classes.chipSelected
                          : classes.chipUnselected
                      }
                      onClick={() => toggleAmenity(name)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Room Rules */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Room Rules
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Set expectations for this room —{" "}
                  {roomRules.length > 0
                    ? `${roomRules.length} selected`
                    : "none selected"}
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Box className={classes.chipGrid}>
                  {RULES_LIST.map((name) => (
                    <Chip
                      key={name}
                      label={roomRules.includes(name) ? `✓ ${name}` : name}
                      clickable
                      variant="outlined"
                      size="small"
                      className={
                        roomRules.includes(name)
                          ? classes.chipSelected
                          : classes.chipUnselected
                      }
                      onClick={() => toggleRule(name)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Pricing
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Base rate and optional price variance by guest count
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      error={Boolean(touched.base_price && errors.base_price)}
                      helperText={touched.base_price && errors.base_price}
                      variant="outlined"
                      required
                      fullWidth
                      label="Base Price per Night (USD)"
                      name="base_price"
                      type="number"
                      inputProps={{ min: 1 }}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>

                {priceVariance.length > 0 && (
                  <Box style={{ marginTop: 16 }}>
                    <Typography
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: TOKENS.textSecondary,
                        marginBottom: 8,
                      }}
                    >
                      Price Variance by Guest Count
                    </Typography>
                    {priceVariance.map((item, index) => (
                      <Box key={index} className={classes.varianceRow}>
                        <TextField
                          className={classes.textField}
                          variant="outlined"
                          label="Guests"
                          type="number"
                          inputProps={{ min: 1 }}
                          value={item.guestCount}
                          onChange={(e) => {
                            const updated = [...priceVariance];
                            updated[index].guestCount = e.target.value;
                            setPriceVariance(updated);
                          }}
                          style={{ width: 120 }}
                        />
                        <TextField
                          className={classes.textField}
                          variant="outlined"
                          label="Price (USD)"
                          type="number"
                          inputProps={{ min: 1 }}
                          value={item.price}
                          onChange={(e) => {
                            const updated = [...priceVariance];
                            updated[index].price = e.target.value;
                            setPriceVariance(updated);
                          }}
                          style={{ flex: 1 }}
                        />
                        <IconButton
                          size="small"
                          style={{ color: "#EF4444" }}
                          onClick={() =>
                            setPriceVariance(
                              priceVariance.filter((_, i) => i !== index),
                            )
                          }
                        >
                          <RemoveIcon style={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box style={{ marginTop: 12 }}>
                  <Button
                    variant="outlined"
                    className={classes.addRowBtn}
                    startIcon={<AddIcon style={{ fontSize: 16 }} />}
                    onClick={() =>
                      setPriceVariance([
                        ...priceVariance,
                        { guestCount: 1, price: "" },
                      ])
                    }
                  >
                    Add Price Variance
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* ── Action Buttons ─────────────────────────────────── */}
            <Box
              display="flex"
              justifyContent="flex-end"
              style={{ marginTop: 8, gap: 12 }}
            >
              <Button
                type="submit"
                disabled={isSubmitting || isSaving}
                variant="outlined"
                className={classes.saveRoomButton}
                startIcon={<AddIcon style={{ fontSize: 18 }} />}
              >
                {isSaving ? "Saving..." : "Save Room & Add Another"}
              </Button>
              <Button
                disabled={savedRooms.length === 0}
                variant="contained"
                className={classes.continueButton}
                endIcon={<ArrowForwardIcon />}
                onClick={() => {
                  if (savedRooms.length === 0) {
                    toast.warning(
                      "Add at least one room before continuing to images.",
                    );
                    return;
                  }
                  // Dispatch the tab change to images — this matches your existing
                  // Redux flow where the stepper moves to "images" tab
                  dispatch(toggleTab("images"));
                }}
              >
                Continue to Images
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
}
